import React from 'react';

// Centred empty-state for a card: a small illustration in the v2 palette (navy-900 #0b1739, cyan-500 #00b5d1,
// cyan-100 #e0f7fb) + title + one line. Styled by .v2-empty in Page.scss; the card it sits in should stretch so it can centre.
type Art = 'caughtUp' | 'registry' | 'investors';

interface Props {
    art: Art;
    title: string;
    sub?: string;
}

const Sparkles: React.FC = () => <>
    <path d="M26 30l2.4 5.6L34 38l-5.6 2.4L26 46l-2.4-5.6L18 38l5.6-2.4L26 30z" fill="#00b5d1" opacity="0.7" />
    <path d="M136 22l1.6 3.8 3.8 1.6-3.8 1.6-1.6 3.8-1.6-3.8-3.8-1.6 3.8-1.6L136 22z" fill="#00b5d1" opacity="0.5" />
    <circle cx="140" cy="92" r="4" fill="#0b1739" opacity="0.15" />
    <circle cx="20" cy="94" r="3" fill="#0b1739" opacity="0.15" />
</>;

const ART: { [key in Art]: JSX.Element } = {
    // checklist with a tick badge — nothing waiting
    caughtUp: <>
        <rect x="50" y="22" width="60" height="80" rx="10" fill="#ffffff" stroke="#0b1739" strokeWidth="3" />
        <rect x="66" y="16" width="28" height="12" rx="4" fill="#0b1739" />
        <rect x="62" y="44" width="10" height="10" rx="3" fill="#00b5d1" />
        <rect x="78" y="46" width="22" height="5" rx="2.5" fill="#cbd5e1" />
        <rect x="62" y="62" width="10" height="10" rx="3" fill="#00b5d1" />
        <rect x="78" y="64" width="18" height="5" rx="2.5" fill="#cbd5e1" />
        <rect x="62" y="80" width="10" height="10" rx="3" fill="#00b5d1" />
        <rect x="78" y="82" width="20" height="5" rx="2.5" fill="#cbd5e1" />
        <circle cx="108" cy="92" r="18" fill="#00b5d1" stroke="#ffffff" strokeWidth="4" />
        <path d="M100 92l6 6 11-12" stroke="#0b1739" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </>,
    // ranked holder bars with a person badge — no registry data yet
    registry: <>
        <rect x="40" y="26" width="80" height="76" rx="10" fill="#ffffff" stroke="#0b1739" strokeWidth="3" />
        <rect x="52" y="40" width="8" height="8" rx="2" fill="#cbd5e1" />
        <rect x="66" y="41" width="42" height="6" rx="3" fill="#00b5d1" />
        <rect x="52" y="58" width="8" height="8" rx="2" fill="#cbd5e1" />
        <rect x="66" y="59" width="30" height="6" rx="3" fill="#00b5d1" opacity="0.7" />
        <rect x="52" y="76" width="8" height="8" rx="2" fill="#cbd5e1" />
        <rect x="66" y="77" width="20" height="6" rx="3" fill="#00b5d1" opacity="0.45" />
        <circle cx="116" cy="90" r="18" fill="#00b5d1" stroke="#ffffff" strokeWidth="4" />
        <circle cx="116" cy="85" r="5" fill="#0b1739" />
        <path d="M107 99c1.5-5 5-7.5 9-7.5s7.5 2.5 9 7.5" stroke="#0b1739" strokeWidth="3.5" strokeLinecap="round" />
    </>,
    // two chat bubbles — no investor activity yet
    investors: <>
        <path d="M34 34a10 10 0 0 1 10-10h54a10 10 0 0 1 10 10v30a10 10 0 0 1-10 10H60l-14 12V74h-2a10 10 0 0 1-10-10V34z" fill="#ffffff" stroke="#0b1739" strokeWidth="3" strokeLinejoin="round" />
        <rect x="48" y="38" width="44" height="6" rx="3" fill="#cbd5e1" />
        <rect x="48" y="52" width="30" height="6" rx="3" fill="#cbd5e1" />
        <path d="M82 70a8 8 0 0 1 8-8h38a8 8 0 0 1 8 8v20a8 8 0 0 1-8 8h-2v10l-12-10H90a8 8 0 0 1-8-8V70z" fill="#00b5d1" stroke="#ffffff" strokeWidth="3" strokeLinejoin="round" />
        <circle cx="98" cy="80" r="3.5" fill="#0b1739" />
        <circle cx="109" cy="80" r="3.5" fill="#0b1739" />
        <circle cx="120" cy="80" r="3.5" fill="#0b1739" />
    </>,
};

const EmptyState: React.FC<Props> = ({ art, title, sub }) =>
    <div className="v2-empty">
        <svg className="illustration" viewBox="0 0 160 128" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <ellipse cx="80" cy="64" rx="62" ry="56" fill="#e0f7fb" />
            <Sparkles />
            {ART[art]}
        </svg>
        <div className="title">{title}</div>
        {sub && <div className="sub">{sub}</div>}
    </div>;

export default EmptyState;
